import type { TrucksState, TruckRaceView, TruckRacerView } from "@townsy/contracts";
import { formatLapTime } from "./state";

/**
 * What the arena's race overlay shows (ADR-0032), worked out from the server's shared state only: the server decides
 * the race (places, laps, times, DNF); these functions only phrase it. Pure, so the HUD tests can check every state.
 */

export function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `${n}${s}`;
}

/** "Lap 2 of 3": the lap a racer is on (the last one once all are done). */
export function lapLabel(r: Pick<TruckRacerView, "laps">, laps: number): string {
  return `Lap ${Math.min(r.laps + 1, laps)} of ${laps}`;
}

export type RaceBanner =
  | { kind: "none" }
  /** A race is about to start: how long is left, whether this visitor is on the grid, people on it so far. */
  | { kind: "lobby"; seconds: number; onGrid: boolean; people: number; field: number }
  /** 3, 2, 1 on the grid. */
  | { kind: "countdown"; count: number }
  /** The first moment of the race. */
  | { kind: "go" }
  /** This visitor is racing: place, lap, the running lap (null before the line) and this race's best lap. */
  | { kind: "racing"; position: number; of: number; lap: number; laps: number; lapMs: number | null; bestLapMs: number | null; raceMs: number }
  /** A race is on without this visitor. */
  | { kind: "watching"; racers: number; leader: string | null };

/** "GO!" shows this long after the green light. */
export const GO_MS = 1200;

export function myRacer(race: TruckRaceView | null | undefined, localActorId: string | null): TruckRacerView | null {
  return race?.racers.find((r) => r.actorId === localActorId) ?? null;
}

/** The big line at the top of the screen. */
export function raceBanner(state: TrucksState | null, localActorId: string | null, serverNow: number): RaceBanner {
  const race = state?.race;
  if (!race || race.phase === "idle" || race.phase === "results") return { kind: "none" };
  const me = myRacer(race, localActorId);
  const left = (at: number | null) => (at === null ? 0 : Math.max(0, Math.ceil((at - serverNow) / 1000)));
  switch (race.phase) {
    case "lobby":
      return { kind: "lobby", seconds: left(race.endsAt), onGrid: me !== null, people: race.racers.length, field: race.field };
    case "countdown":
      return { kind: "countdown", count: Math.max(1, left(race.endsAt)) };
    case "racing": {
      const raceMs = race.startedAt === null ? 0 : Math.max(0, serverNow - race.startedAt);
      if (!me) return { kind: "watching", racers: race.racers.length, leader: race.racers[0]?.label ?? null };
      if (me.finishMs !== null || me.dnf) return { kind: "none" }; // the finish screen takes over
      if (raceMs < GO_MS) return { kind: "go" };
      const lapStartedAt = state?.drivers.find((d) => d.actorId === localActorId)?.lapStartedAt ?? null;
      // The lap clock runs from the line (lap 1 starts when the truck first crosses it after the green light).
      const lapMs = lapStartedAt !== null && lapStartedAt >= race.startedAt! ? Math.max(0, serverNow - lapStartedAt) : null;
      return { kind: "racing", position: me.position, of: race.racers.length, lap: Math.min(me.laps + 1, race.laps), laps: race.laps, lapMs, bestLapMs: me.bestLapMs, raceMs };
    }
  }
}

export interface FinishRow {
  position: number;
  label: string;
  ai: boolean;
  truckId: string;
  /** "1:42.3", "DNF", or the lap a racer still out there is on. */
  time: string;
  best: string;
  mine: boolean;
}

export interface FinishView {
  /** The race (its green light, server time): a new race replaces the screen; a dismissed one stays closed. */
  raceKey: number;
  place: number;
  of: number;
  dnf: boolean;
  finishMs: number | null;
  bestLapMs: number | null;
  /** Everyone is home (the results); before that the others' rows are still live. */
  final: boolean;
  headline: string;
  rows: FinishRow[];
}

/**
 * The finish screen for this visitor: once they crossed the line on the last lap (or are out of the race), and with
 * the full results when the race is over. Null when they are not in a race that has started.
 */
export function finishView(race: TruckRaceView | null | undefined, localActorId: string | null): FinishView | null {
  if (!race || (race.phase !== "racing" && race.phase !== "results") || race.startedAt === null) return null;
  const me = myRacer(race, localActorId);
  if (!me) return null;
  const final = race.phase === "results";
  if (!final && me.finishMs === null && !me.dnf) return null;
  const rows: FinishRow[] = race.racers.map((r) => ({
    position: r.position,
    label: r.label,
    ai: r.ai,
    truckId: r.truckId,
    time: r.finishMs !== null ? formatLapTime(r.finishMs) : r.dnf ? "DNF" : lapLabel(r, race.laps),
    best: r.bestLapMs !== null ? formatLapTime(r.bestLapMs) : "–",
    mine: r.actorId === localActorId,
  }));
  const of = race.racers.length;
  let headline: string;
  if (me.dnf) headline = "Did not finish";
  else if (me.finishMs === null) headline = "Race over";
  else if (me.position === 1) headline = of > 1 ? `You won! 1st of ${of}` : "Time trial complete";
  else headline = `You finished ${ordinal(me.position)} of ${of}`;
  return { raceKey: race.startedAt, place: me.position, of, dnf: me.dnf, finishMs: me.finishMs, bestLapMs: me.bestLapMs, final, headline, rows };
}

/**
 * The finish screen stays up after the results board is gone (the server clears the race 15 s after the finish) until
 * the visitor picks "Race again" or "Free drive", or a new race of theirs begins. One page, one arena: module state.
 */
let kept: FinishView | null = null;
let dismissedKey: number | null = null;

export const finishMemory = {
  /** The screen to show now, given the latest race view. */
  current(race: TruckRaceView | null | undefined, localActorId: string | null): FinishView | null {
    const live = finishView(race, localActorId);
    if (live) kept = live;
    else if (race && (race.phase === "lobby" || race.phase === "countdown") && myRacer(race, localActorId)) kept = null;
    return kept && kept.raceKey !== dismissedKey ? kept : null;
  },
  dismiss(): void {
    if (kept) dismissedKey = kept.raceKey;
  },
  reset(): void {
    kept = null;
    dismissedKey = null;
  },
};
